/**
 * ConversionSummary — Conversion Engine reporting Report A (Stage 4).
 *
 * Per-event conversion funnel: vouchers issued / new-member / activated /
 * redeemed, friends linked vs activated, booth attendance, and current +
 * redeemed value. Only events that have conversion vouchers appear. No business
 * policy here (promoter fee / MSR credit are Reports B/C).
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getConversionSummaryByEvent,
  type ConversionSummaryRow,
} from "../../services/conversionReports.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";

const PAGE_SIZE = 50;

const n = (v: unknown): number => Number(v ?? 0);

const money = (v: unknown): string => `$${n(v).toFixed(2)}`;

const formatDate = (d?: string | null): string => {
  if (!d) {
    return "";
  }
  try {
    // event_start_date is a plain DATE — render it as a literal calendar date.
    return new Date(`${d}T00:00:00`).toLocaleDateString();
  } catch {
    return d;
  }
};

const filterToRpc = (f: ReportFilterState, page: number, limit: number) => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  state: f.state,
  type: f.type,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

const EXPORT_COLUMNS = [
  "Event ID",
  "Event Name",
  "Event Date",
  "Type",
  "State",
  "Vouchers Issued",
  "New-Member Vouchers",
  "Activated",
  "Redeemed",
  "Friends Linked",
  "Friends Activated",
  "Attendance",
  "Current Value",
  "Redeemed Value",
];

const ConversionSummary: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.state,
    filters.type,
    filters.orgId,
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-conversion-summary", filters, page],
    queryFn: () =>
      getConversionSummaryByEvent(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const mapRows = (
    pageRows: ConversionSummaryRow[],
  ): Record<string, unknown>[] =>
    pageRows.map((r) => ({
      "Event ID": r.event_id,
      "Event Name": r.event_title,
      "Event Date": formatDate(r.event_start_date),
      Type: r.event_type,
      State: r.event_location_state || "",
      "Vouchers Issued": n(r.vouchers_issued),
      "New-Member Vouchers": n(r.new_member_vouchers),
      Activated: n(r.vouchers_activated),
      Redeemed: n(r.vouchers_redeemed),
      "Friends Linked": n(r.friends_linked),
      "Friends Activated": n(r.friends_activated),
      Attendance: n(r.attendance_count),
      "Current Value": n(r.current_value_total).toFixed(2),
      "Redeemed Value": n(r.redeemed_value_total).toFixed(2),
    }));

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Conversion Summary by Event"
        description={
          <>
            Per-event Conversion Engine funnel — invite/reward vouchers issued,
            activated, and redeemed; friends linked vs. activated; booth
            attendance; and value. Only events with conversion vouchers appear.
          </>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getConversionSummaryByEvent(filterToRpc(f, p, l)).then((res) => ({
                rows: res.rows,
                total: res.total,
              }))
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="conversion-summary-by-event"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showStatus={false}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2 text-right">Issued</th>
              <th className="px-3 py-2 text-right">New Members</th>
              <th className="px-3 py-2 text-right">Activated</th>
              <th className="px-3 py-2 text-right">Redeemed</th>
              <th className="px-3 py-2 text-right">Friends (link/act)</th>
              <th className="px-3 py-2 text-right">Attendance</th>
              <th className="px-3 py-2 text-right">Current $</th>
              <th className="px-3 py-2 text-right">Redeemed $</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-6 text-center text-gray-500"
                >
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-6 text-center text-gray-500"
                >
                  No events with conversion vouchers yet.
                </td>
              </tr>
            )}
            {rows.map((r: any) => (
              <tr key={r.event_id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <Link
                    to={`/events/${r.event_id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {r.event_title}
                  </Link>
                </td>
                <td className="px-3 py-2">{formatDate(r.event_start_date)}</td>
                <td className="px-3 py-2">{r.event_type}</td>
                <td className="px-3 py-2">{r.event_location_state || ""}</td>
                <td className="px-3 py-2 text-right">{n(r.vouchers_issued)}</td>
                <td className="px-3 py-2 text-right">
                  {n(r.new_member_vouchers)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.vouchers_activated)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.vouchers_redeemed)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.friends_linked)} / {n(r.friends_activated)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.attendance_count)}
                </td>
                <td className="px-3 py-2 text-right">
                  {money(r.current_value_total)}
                </td>
                <td className="px-3 py-2 text-right">
                  {money(r.redeemed_value_total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversionSummary;
